/**
 * sqlite3mt — Client Worker VFS for multi-worker SQLite architecture
 *
 * §96 §10.3 (xReadMainDb), §10.5 (xWrite), §11.1 (gates & WAL_WRITE),
 * §11.1.3 (WAL-write/commit-sync path), §17 (xShmBarrier).
 *
 * This module implements the SQLite VFS interface methods that each Client
 * Worker uses to interact with the shared SABs and the Control Worker.
 *
 * Architecture:
 *   - Each Client Worker runs SQLite with this VFS registered.
 *   - SQLite calls xOpen → routes to MainDb (SAB1 slot window), WAL (SAB2),
 *     or SHM (SAB3).
 *   - xReadMainDb uses pinOrInstallSlotForReadLinearized + Atomics.wait for
 *     slot resolution (§10.3).
 *   - xWrite(WAL) copies frames into SAB2; on commit-frame close, extracts
 *     pages and posts wal-commit-sync to Control Worker (§11.1.3).
 *   - xShmBarrier does masked push (only words the caller owns) and full pull
 *     (§17).
 *   - WAL_WRITE ownership is acquired without assuming that every slot-0
 *     lock is a user commit; commit-specific gate/snapshot work happens
 *     only before the first WAL byte is written (§11.1.2–§11.1.3).
 *
 * Dependencies:
 *   - types.ts for ClientWorkerConnState, ReaderVisibleFrontier,
 *     WalCommitSyncMessage, SlotState
 *   - sab-layout.ts for SAB byte offsets and typed views
 *   - slot.ts for pinOrInstallSlotForReadLinearized
 *   - client-commit-sync.ts for extractCommittedPagesAndCommitSizeBackwardCopy,
 *     SQLITE_OK/BUSY/IOERR constants
 *   - control.ts for waitUntilControlReadyOrFatal, gate helpers
 *   - worker-bridge.ts for FaultRequestMessage
 *   - shm-ops.ts for readWalSnapshotStable
 */

import {
    acquireWalWriteLock as acquireWalWriteLockCore,
    prepareCommitWriteBeforeFirstWalByte as prepareCommitWriteBeforeFirstWalByteCore,
    releaseWalWriteAfterCommitSync as releaseWalWriteAfterCommitSyncCore,
    SQLITE_BUSY,
    SQLITE_IOERR,
    SQLITE_OK,
} from './client-commit-sync'
import { waitUntilControlReadyOrFatal } from './control'
import { waitUntilReaderFrontierVisible } from './frontier'
import {
    WAL_CKPT_BYTES,
    WAL_CKPT_NBACKFILL_ATTEMPTED_WORD,
    WAL_CKPT_NBACKFILL_WORD,
    WAL_CKPT_READMARK_WORD0,
    WAL_CKPT_REGION_OFF,
    WAL_INDEX_HDR_WORDS,
    readWalSnapshotStable,
    sab3ShmInt32View,
} from './shm-ops'
import {
    getSlotGen,
    getSlotPgno,
    getSlotState,
    getSlotWaitSeq,
    pinOrInstallSlotForReadLinearized,
    readSlotDataInto,
    unlockSlot,
    xWriteMainDb as xWriteMainDbSlotCache,
} from './slot'
import { buildFaultRequestMessage, type FaultRequestMessage } from './worker-bridge'
import {
    ensureSab2WalCapacity,
    NUM_SLOTS,
    SAB2_DATA_OFF,
    Sab4Scalar,
    sab2MaxWalByteLength,
    SlotMetaI32,
    sab2SizeView,
    sab4ScalarView,
    sab1SlotMetaView,
} from './sab-layout'
import { sdkConsole } from '../logger'
import type { PinResultSlot } from './slot'
import {
    SlotState,
    WAL_FRAME_HEADER_SIZE,
    WAL_HEADER_SIZE,
    type ClientWorkerConnState,
    type PageHash,
    type Pgno,
    type ReaderVisibleFrontier,
    type WalCommitSyncMessage,
} from './types'

// ── VFS-local SQLite return codes (not in client-commit-sync) ────

export const SQLITE_NOTFOUND = 12
export const SQLITE_FULL = 13
export const SQLITE_IOERR_SHORT_READ = (10 | (2 << 8))

// ── xOpen flags ──────────────────────────────────────────────────

export const SQLITE_OPEN_MAIN_DB = 0x00000100
export const SQLITE_OPEN_WAL = 0x00080000
export const SQLITE_OPEN_MAIN_JOURNAL = 0x00000800
export const SQLITE_OPEN_SUBJOURNAL = 0x00001000
export const SQLITE_OPEN_CREATE = 0x00000004

// ── xShmLock flags ───────────────────────────────────────────────

export const SQLITE_SHM_UNLOCK = 1
export const SQLITE_SHM_LOCK = 2
export const SQLITE_SHM_SHARED = 4
export const SQLITE_SHM_EXCLUSIVE = 8

// ── File handle types ────────────────────────────────────────────

/**
 * File handle type returned by xOpen.
 * Routes I/O to the correct SAB region.
 */
export const enum VfsFileType {
    MainDb = 1,
    Wal = 2,
    Journal = 3,
    Shm = 4,
    Other = 5,
}

/**
 * Per-file state tracked by the VFS.
 */
export interface VfsFileState {
    type: VfsFileType
}

// ── §17 xShmBarrier mask computation ────────────────────────────

/**
 * Compute the allowed-write mask for xShmBarrier.
 *
 * §17: only words the caller legitimately owns (by SHM lock state)
 * may be pushed from local WASM shadow to SAB3. All words are pulled.
 *
 * @param myLocks - current SHM lock state for this connection
 * @param walIndexWordCount - number of i32 words in the mapped wal-index view
 * @returns set of i32 indices that may be pushed
 */
function lockHeld(myLocks: Int8Array | number[], index: number): boolean {
    const state = myLocks[index]
    return typeof state === 'number' && state >= 0
}

function addWordIfInRange(mask: Set<number>, wordIndex: number, wordCount: number): void {
    if (wordIndex >= 0 && wordIndex < wordCount) {
        mask.add(wordIndex)
    }
}

function addWordRange(mask: Set<number>, start: number, endExclusive: number, wordCount: number): void {
    const from = Math.max(0, start)
    const to = Math.min(endExclusive, wordCount)
    for (let wordIndex = from; wordIndex < to; wordIndex += 1) {
        mask.add(wordIndex)
    }
}

export function allowedBarrierMask(
    myLocks: Int8Array | number[],
    walIndexWordCount: number,
): Set<number> {
    const mask = new Set<number>()
    const ckptWordBase = WAL_CKPT_REGION_OFF / Int32Array.BYTES_PER_ELEMENT
    const hashWordBase = ckptWordBase + (WAL_CKPT_BYTES / Int32Array.BYTES_PER_ELEMENT)

    // WRITE or RECOVER: caller owns both WalIndexHdr copies + hash tables,
    // but not the checkpoint block between them.
    if (lockHeld(myLocks, 0) || lockHeld(myLocks, 2)) {
        addWordRange(mask, 0, WAL_INDEX_HDR_WORDS * 2, walIndexWordCount)
        addWordRange(mask, hashWordBase, walIndexWordCount, walIndexWordCount)
    }

    // CKPT lock: caller owns nBackfill + nBackfillAttempted words
    if (lockHeld(myLocks, 1)) {
        addWordIfInRange(mask, ckptWordBase + WAL_CKPT_NBACKFILL_WORD, walIndexWordCount)
        addWordIfInRange(mask, ckptWordBase + WAL_CKPT_NBACKFILL_ATTEMPTED_WORD, walIndexWordCount)
    }

    // Read-mark: each connection may update its own readMark
    // readMark[i] lives in the checkpoint block after the nBackfill word.
    for (let i = 0; i < 5; i++) {
        const lockIdx = 3 + i
        if (lockHeld(myLocks, lockIdx)) {
            addWordIfInRange(mask, ckptWordBase + WAL_CKPT_READMARK_WORD0 + i, walIndexWordCount)
        }
    }

    return mask
}

// ── §10.3 Reader frontier / fault request helpers ──────────────

/**
 * Extract the reader-visible frontier that §10.3 must preserve through the
 * xReadMainDb fault/retry loop.
 */
export function getReaderVisibleFrontier(
    conn: Pick<ClientWorkerConnState, 'readerSalt0' | 'readerSalt1' | 'readerFrameMax'>
): ReaderVisibleFrontier {
    return {
        readerSalt0: conn.readerSalt0,
        readerSalt1: conn.readerSalt1,
        readerFrameMax: conn.readerFrameMax,
    }
}

/**
 * Build the runtimeEpoch-stamped fault message for a pinned slot miss.
 *
 * This keeps the visible reader frontier coupled to the request so the future
 * xReadMainDb implementation can delegate fault dispatch without rebuilding
 * the message ad hoc.
 */
export function buildFaultRequestFromPinnedSlot(
    conn: Pick<ClientWorkerConnState, 'attachedRuntimeEpoch' | 'readerSalt0' | 'readerSalt1' | 'readerFrameMax'>,
    pgno: Pgno,
    pin: Pick<PinResultSlot, 'slot' | 'gen'>
): FaultRequestMessage {
    const frontier = getReaderVisibleFrontier(conn)
    return buildFaultRequestMessage(conn.attachedRuntimeEpoch, {
        pgno,
        slot: pin.slot,
        gen: pin.gen,
        visibleFrameMax: frontier.readerFrameMax,
        visibleWalSalt0: frontier.readerSalt0,
        visibleWalSalt1: frontier.readerSalt1,
    })
}

function yieldBeforeRetryingSlotInstall(sab1: SharedArrayBuffer): void {
    // §10.2 requires yield/retry when every slot is temporarily fault-owned.
    // Waiting on any active slot's waitseq avoids a hot spin while still
    // waking on publishFaultState() and fatal wakeAllSlotWaiters().
    for (let slot = 0; slot < NUM_SLOTS; slot += 1) {
        const meta = sab1SlotMetaView(sab1, slot)
        const state = Atomics.load(meta, SlotMetaI32.STATE)

        if (state !== SlotState.FETCHING && state !== SlotState.RETRY) {
            continue
        }

        const waitSeq = Atomics.load(meta, SlotMetaI32.WAITSEQ)
        if (Atomics.load(meta, SlotMetaI32.STATE) !== state) {
            return
        }

        Atomics.wait(meta, SlotMetaI32.WAITSEQ, waitSeq, 1)
        return
    }
}

export function xReadMainDb(
    conn: ClientWorkerConnState,
    sab1: SharedArrayBuffer,
    sab4: SharedArrayBuffer,
    pgno: number,
    pDst: Uint8Array,
    postFault: (msg: FaultRequestMessage) => void,
    pageOffset: number = 0,
): number {
    const scalar = sab4ScalarView(sab4)

    for (;;) {
        if (waitUntilControlReadyOrFatal(sab4, conn.attachedRuntimeEpoch) === 'FATAL') {
            return SQLITE_IOERR
        }

        if (Atomics.load(scalar, Sab4Scalar.FATAL_FLAG) !== 0) {
            return SQLITE_IOERR
        }

        if (waitUntilReaderFrontierVisible(
            sab4,
            conn.readerSalt0,
            conn.readerSalt1,
            conn.readerFrameMax
        ) === 'FATAL') {
            return SQLITE_IOERR
        }

        const pin = pinOrInstallSlotForReadLinearized(pgno, sab1)
        if (pin.kind === 'EOF') {
            pDst.fill(0)
            return SQLITE_IOERR_SHORT_READ
        }
        if (pin.kind === 'RETRY') {
            yieldBeforeRetryingSlotInstall(sab1)
            continue
        }

        let req: FaultRequestMessage | null = null
        let slotWaitSeq = 0
        let needRetry = false
        const { slot, gen, firstOwner } = pin

        try {
            if (getSlotPgno(sab1, slot) !== pgno || getSlotGen(sab1, slot) !== gen) {
                needRetry = true
            } else {
                const st = getSlotState(sab1, slot)

                if (st === SlotState.READY) {
                    readSlotDataInto(pDst, sab1, slot, undefined, pageOffset)
                    return SQLITE_OK
                }

                if (st === SlotState.ERROR) {
                    return SQLITE_IOERR
                }

                slotWaitSeq = getSlotWaitSeq(sab1, slot)
                if (st === SlotState.RETRY || firstOwner) {
                    if (st === SlotState.RETRY) {
                        Atomics.store(sab1SlotMetaView(sab1, slot), SlotMetaI32.STATE, SlotState.FETCHING)
                    }
                    req = buildFaultRequestFromPinnedSlot(conn, pgno as Pgno, pin)
                }
            }
        } finally {
            unlockSlot(sab1, slot)
        }

        if (needRetry) {
            continue
        }

        if (req != null) {
            postFault(req)
        }

        if (Atomics.load(scalar, Sab4Scalar.FATAL_FLAG) !== 0) {
            return SQLITE_IOERR
        }

        Atomics.wait(sab1SlotMetaView(sab1, slot), SlotMetaI32.WAITSEQ, slotWaitSeq)
    }
}

// ── §10.5 / §11.1 concrete write-path wrappers ──────────────────

export interface AcquireWalWriteLockHooks {
    tryAcquireWalWriteLock(): boolean
    enterFatalIoModeForConnection(): void
}

export interface PrepareCommitWriteBeforeFirstWalByteHooks {
    enterFatalIoModeForConnection(): void
}

export interface ReleaseWalWriteAfterCommitHooks {
    hashPage(pageData: Uint8Array): PageHash
    postCommitSync(message: WalCommitSyncMessage, transfer: ArrayBuffer[]): void
    enterFatalIoModeForConnection(): void
    releaseWalWriteLock(): void
}

function sab2WalBytesView(sab2: SharedArrayBuffer): Uint8Array {
    return new Uint8Array(sab2, SAB2_DATA_OFF, sab2.byteLength - SAB2_DATA_OFF)
}

function readU32BE(bytes: Uint8Array, offset: number): number {
    return (
        ((bytes[offset]! << 24) >>> 0)
        | (bytes[offset + 1]! << 16)
        | (bytes[offset + 2]! << 8)
        | bytes[offset + 3]!
    ) >>> 0
}

function decodeWalHeaderPageSize(sab2: SharedArrayBuffer): number {
    const walSize = Atomics.load(sab2SizeView(sab2), 0)
    if (walSize < WAL_HEADER_SIZE) {
        throw new RangeError('WAL header is not fully present in SAB2')
    }

    const walBytes = sab2WalBytesView(sab2)
    const raw = readU32BE(walBytes, 8)
    return raw === 1 ? 65536 : raw
}

function commitFrameNoFromWrite(frameOffset: number, frameSize: number): number | null {
    if (!Number.isInteger(frameOffset) || frameOffset < WAL_HEADER_SIZE) {
        return null
    }
    if (!Number.isInteger(frameSize) || frameSize <= WAL_FRAME_HEADER_SIZE) {
        return null
    }

    const frameOffsetWithinWal = frameOffset - WAL_HEADER_SIZE
    if (frameOffsetWithinWal % frameSize !== 0) {
        return null
    }

    return (frameOffsetWithinWal / frameSize) + 1
}

export function xWriteMainDb(
    _conn: ClientWorkerConnState,
    sab1: SharedArrayBuffer,
    pgno: number,
    pSrc: Uint8Array,
    pageOffset = 0,
    pageSize: number | undefined = undefined,
): number {
    xWriteMainDbSlotCache(pgno, pSrc, sab1, pageSize, pageOffset)
    return SQLITE_OK
}

export function xWriteWal(
    conn: ClientWorkerConnState,
    sab2: SharedArrayBuffer,
    frameOffset: number,
    frameData: Uint8Array,
    isCommitFrame: boolean,
    explicitCommitFrameNo: number | null = null,
): number {
    const writeEnd = frameOffset + frameData.length
    if (!Number.isInteger(frameOffset) || frameOffset < 0) {
        return SQLITE_IOERR
    }
    if (!ensureSab2WalCapacity(sab2, writeEnd)) {
        sdkConsole.error('[sqlite3mt:vfs] WAL shadow capacity exhausted', {
            connId: conn.connId,
            requiredWalBytes: writeEnd,
            frameOffset,
            frameBytes: frameData.length,
            currentWalBytes: Atomics.load(sab2SizeView(sab2), 0),
            currentSab2Bytes: sab2.byteLength,
            maxWalBytes: sab2MaxWalByteLength(sab2),
            isCommitFrame,
            explicitCommitFrameNo,
            pendingCommitFrame: conn.pendingCommitFrame,
            txStartFrame: conn.txStartFrame,
            holdsWalWrite: conn.holdsWalWrite,
        })
        return SQLITE_FULL
    }

    const walBytes = sab2WalBytesView(sab2)
    const commitFrameNo = isCommitFrame
        ? (explicitCommitFrameNo ?? commitFrameNoFromWrite(frameOffset, frameData.length))
        : null
    if (isCommitFrame && commitFrameNo == null) {
        return SQLITE_IOERR
    }

    walBytes.set(frameData, frameOffset)

    const sizeView = sab2SizeView(sab2)
    if (frameOffset === 0 && frameData.length >= WAL_HEADER_SIZE) {
        Atomics.store(sizeView, 0, frameData.length)
    } else if (writeEnd > Atomics.load(sizeView, 0)) {
        Atomics.store(sizeView, 0, writeEnd)
    }

    if (commitFrameNo != null) {
        conn.pendingCommitFrame = commitFrameNo
    }

    return SQLITE_OK
}

export function acquireWalWriteLock(
    conn: ClientWorkerConnState,
    sab4: SharedArrayBuffer,
    hooks: AcquireWalWriteLockHooks,
): number {
    return acquireWalWriteLockCore({
        connState: conn,
        sab4,
        tryAcquireWalWriteLock: hooks.tryAcquireWalWriteLock,
        enterFatalIoModeForConnection: hooks.enterFatalIoModeForConnection,
    })
}

export function prepareCommitWriteBeforeFirstWalByte(
    conn: ClientWorkerConnState,
    sab3: SharedArrayBuffer,
    sab4: SharedArrayBuffer,
    hooks: PrepareCommitWriteBeforeFirstWalByteHooks,
): number {
    return prepareCommitWriteBeforeFirstWalByteCore({
        connState: conn,
        sab4,
        readWalSnapshotStable: () => readWalSnapshotStable(sab3ShmInt32View(sab3)),
        enterFatalIoModeForConnection: hooks.enterFatalIoModeForConnection,
    })
}

export function releaseWalWriteAfterCommit(
    conn: ClientWorkerConnState,
    sab2: SharedArrayBuffer,
    sab3: SharedArrayBuffer,
    sab4: SharedArrayBuffer,
    hooks: ReleaseWalWriteAfterCommitHooks,
): number {
    try {
        const pageSize = decodeWalHeaderPageSize(sab2)
        return releaseWalWriteAfterCommitSyncCore({
            connState: conn,
            sab2,
            sab4,
            hashPage: hooks.hashPage,
            readWalSnapshotStable: () => readWalSnapshotStable(sab3ShmInt32View(sab3)),
            postMessageToControl: hooks.postCommitSync,
            enterFatalIoModeForConnection: hooks.enterFatalIoModeForConnection,
            releaseWalWriteLock: hooks.releaseWalWriteLock,
            pageSize,
        })
    } catch {
        conn.fatalIoMode = true
        hooks.enterFatalIoModeForConnection()
        return SQLITE_IOERR
    }
}

// ── VFS entry points (§10.3, §10.5, §11.1, §17) ─────────────────

/**
 * Client Worker VFS — the interface that sqlite3-wasm calls into.
 *
 * Each method corresponds to a SQLite VFS method. The implementation
 * uses SABs for shared state and posts messages to the Control Worker
 * for fault resolution and commit registration.
 *
 * These are pure functions/static methods that take the SAB buffers
 * and connection state as parameters, keeping the VFS stateless aside
 * from per-connection state.
 *
 * Build phase will implement each method body following the spec pseudocode.
 */
export interface ClientWorkerVfs {
    /**
     * §10.3: Read a page from MainDb via slot protocol.
     *
     * 1. Wait for CONTROL_READY
     * 2. Wait for reader frontier
     * 3. pinOrInstallSlotForReadLinearized(pgno)
     * 4. If READY: memcpy and return SQLITE_OK
     * 5. If FETCHING/RETRY: post fault to Control Worker, Atomics.wait
     * 6. If EOF: return SQLITE_IOERR_SHORT_READ
     */
    xReadMainDb(
        conn: ClientWorkerConnState,
        sab1: SharedArrayBuffer,
        sab4: SharedArrayBuffer,
        pgno: number,
        pDst: Uint8Array,
        postFault: (msg: FaultRequestMessage) => void,
    ): number

    /**
     * §10.5: Write a page to MainDb slot cache in SAB1.
     *
     * This is a cache-write, not authoritative. The VFS may update a READY
     * slot or evict EMPTY/ERROR slots. Returns SQLITE_OK even if no slot
     * could be updated.
     */
    xWriteMainDb(
        conn: ClientWorkerConnState,
        sab1: SharedArrayBuffer,
        pgno: number,
        pSrc: Uint8Array,
    ): number

    /**
     * §11.1.3: Write a WAL frame to SAB2.
     *
     * Copies frame data from WASM to SAB2. If this frame is a commit frame
     * (has commit flag set), records pendingCommitFrame.
     */
    xWriteWal(
        conn: ClientWorkerConnState,
        sab2: SharedArrayBuffer,
        frameOffset: number,
        frameData: Uint8Array,
        isCommitFrame: boolean,
    ): number

    /**
     * §11.1.2: Acquire raw WAL_WRITE ownership.
     *
     * This mirrors SQLite's lock semantics only. It must not assume that
     * every slot-0 acquire is a user commit, because SQLite also takes
     * WAL_WRITE during wal-index recovery/header repair.
     */
    acquireWalWriteLock(
        conn: ClientWorkerConnState,
        sab4: SharedArrayBuffer,
        hooks: AcquireWalWriteLockHooks,
    ): number

    /**
     * §11.1.3: Commit-specific gate/snapshot init immediately before the
     * first WAL byte is written.
     */
    prepareCommitWriteBeforeFirstWalByte(
        conn: ClientWorkerConnState,
        sab3: SharedArrayBuffer,
        sab4: SharedArrayBuffer,
        hooks: PrepareCommitWriteBeforeFirstWalByteHooks,
    ): number

    /**
     * §11.1.3: Release WAL_WRITE after commit, with commit-sync protocol.
     *
     * If pendingCommitFrame > 0:
     * 1. Extract committed pages from SAB2
     * 2. Compute page hashes and blobs
     * 3. postMessage(wal-commit-sync) to Control Worker
     * 4. Atomics.wait on COMMIT_ACK_SEQ until ACK received
     * 5. Check ACK_RC
     *
     * In all cases, decrement WAL_WRITE_HOLDERS and release WAL_WRITE in finally.
     */
    releaseWalWriteAfterCommit(
        conn: ClientWorkerConnState,
        sab2: SharedArrayBuffer,
        sab3: SharedArrayBuffer,
        sab4: SharedArrayBuffer,
        hooks: ReleaseWalWriteAfterCommitHooks,
    ): number

    /**
     * §17: Masked push, full pull.
     *
     * For each SHM region:
     * 1. Compute allowed mask from myLocks
     * 2. Push dirty words in mask from WASM shadow to SAB3
     * 3. Pull all words from SAB3 to WASM shadow and clean baseline
     */
    xShmBarrier(
        conn: ClientWorkerConnState,
        sab3: SharedArrayBuffer,
        wasmShadow: Int32Array[],
        cleanBaseline: Int32Array[],
    ): void

    /**
     * §4.3: Map SHM region.
     * Returns a view into SAB3 at the requested region index.
     */
    xShmMap(
        sab3: SharedArrayBuffer,
        regionIndex: number,
        regionSize: number,
    ): Int32Array

    /**
     * §4.3: Acquire/release SHM locks.
     * Translates SQLite xShmLock calls to SAB3 lock region Atomics operations.
     */
    xShmLock(
        conn: ClientWorkerConnState,
        sab3: SharedArrayBuffer,
        offset: number,
        n: number,
        flags: number,
    ): number

    /**
     * §4.3: Unmap SHM regions.
     */
    xShmUnmap(): void
}
